<template>
  <div class="project-management">
    <p v-if="projectLoadError" role="alert" class="mb-3 text-sm text-red-700">{{ projectLoadError }}</p>
    <!-- Shell Header -->
    <div class="bg-white border border-gray-200 mb-3">
      <ComponentHeader
        :class="{ sensitive: !!selectedProject }"
        :title="selectedProject ? selectedProject.name : 'Projects'"
        :heading-level="3"
      >
        <template v-if="selectedProject" #actions>
          <button
            @click="openEditModal"
            class="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded transition-colors"
            title="Edit name and description"
          >
            <font-awesome-icon :icon="['fas', 'pencil']" class="text-xs" />
          </button>
        </template>

        <template v-if="selectedProject" #subtitle>
          <span
            v-if="isOngoing"
            class="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700"
          >
            Ongoing
          </span>
          <span
            v-if="selectedProject.owner_entity_type_name"
            class="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700"
            title="Tasks auto-assign to the user linked to an ancestor entity of this type"
          >
            Owner: {{ selectedProject.owner_entity_type_name }}
          </span>
          <span v-if="selectedProject.description || dateRangeText">
            {{ selectedProject.description }}<span v-if="dateRangeText" class="text-gray-500"> ({{ dateRangeText }})</span>
          </span>
        </template>

        <select
          v-model="selectedProjectId"
          @change="onProjectChange"
          class="px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400 min-w-[16rem]"
        >
          <option :value="null">Choose a project...</option>
          <option v-for="project in projects" :key="project.id" :value="project.id">
            {{ project.name }}<span v-if="project.project_type === 'ONGOING'"> (Ongoing)</span>
          </option>
        </select>
        <button @click="openCreateModal" class="btn-primary-sm">
          <font-awesome-icon :icon="['fas', 'plus']" class="text-xs mr-1" />
          Create Project
        </button>

        <template v-if="selectedProject && isOngoing" #bottom>
          <Page_Tabs
            :tabs="subTabs"
            :initialTab="currentSubTab"
            @select="onSubTabSelect"
            no-border
            class="px-4 pt-1"
          />
        </template>
      </ComponentHeader>
    </div>

    <!-- Empty state -->
    <div v-if="!selectedProject && !loadingProjects" class="bg-white border border-gray-200 p-8 text-center">
      <p class="text-xs text-gray-500">Select a project from the dropdown above, or create a new one to get started.</p>
    </div>

    <!-- Sub-view content -->
    <ProjectPerformanceView
      v-if="selectedProject && currentSubView === 'view'"
      :key="`view-${selectedProject.id}`"
      :project="selectedProject"
    />

    <ManageProjectsView
      v-if="selectedProject && currentSubView === 'manage'"
      :key="`manage-${selectedProject.id}`"
      :project="selectedProject"
      :action-types="actionTypes"
      @project-updated="reloadProject"
    />

    <!-- Create Project Modal -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeCreateModal"
    >
      <div class="bg-white rounded shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <CreateProject
          @project-created="onProjectCreated"
          @cancel="closeCreateModal"
        />
      </div>
    </div>

    <!-- Edit Project Header Modal -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeEditModal"
    >
      <div class="bg-white rounded shadow-lg max-w-2xl w-full mx-4">
        <div class="px-4 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-800">Edit Project</h3>
        </div>
        <div class="p-4 space-y-3">
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Name</label>
            <input
              v-model="editForm.name"
              type="text"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              v-model="editForm.description"
              rows="3"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">
              Owner Entity Type
              <span class="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              v-model="editForm.owner_entity_type"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
            >
              <option :value="null">No automatic owner</option>
              <option v-for="type in entityTypes" :key="type.id" :value="type.id">
                {{ type.type_name }}
              </option>
            </select>
            <p class="text-xs text-gray-500 mt-1">
              Applies only to tasks created after this change.
            </p>
          </div>
          <p v-if="editError" class="text-xs text-red-600">{{ editError }}</p>
        </div>
        <div class="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button @click="closeEditModal" class="btn-secondary-sm" :disabled="savingEdit">Cancel</button>
          <button
            @click="saveEdit"
            :disabled="!editForm.name.trim() || savingEdit"
            class="btn-primary-sm"
          >
            {{ savingEdit ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import CreateProject from './CreateProject.vue';
import ProjectPerformanceView from './ProjectPerformanceView.vue';
import ManageProjectsView from './ManageProjectsView.vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import { calendarDate } from '@/utils/common';

const SUB_VIEW_BY_TAB = { 'View': 'view', 'Manage': 'manage' };
const TAB_BY_SUB_VIEW = { view: 'View', manage: 'Manage' };

export default {
  name: 'ProjectTable',

  components: {
    FontAwesomeIcon,
    CreateProject,
    ProjectPerformanceView,
    ManageProjectsView,
    Page_Tabs,
    ComponentHeader
  },

  data() {
    return {
      projects: [],
      selectedProjectId: null,
      selectedProject: null,
      loadingProjects: false,
      projectLoadError: '',
      actionTypes: [],
      entityTypes: [],
      subTabs: ['View', 'Manage'],
      currentSubView: 'view',
      showCreateModal: false,
      showEditModal: false,
      editForm: { name: '', description: '', owner_entity_type: null },
      savingEdit: false,
      editError: ''
    };
  },

  computed: {
    selectionStorageKey() { return `graph_projects_selected_id:${this.$store.state.tenant.current_tenant_slug}:${this.$store.getters['auth/current_user']?.id || ''}`; },
    isOngoing() {
      return this.selectedProject?.project_type === 'ONGOING';
    },
    currentSubTab() {
      return TAB_BY_SUB_VIEW[this.currentSubView];
    },
    dateRangeText() {
      if (!this.selectedProject) return '';
      const start = this.formatDate(this.selectedProject.start_date);
      const end = this.formatDate(this.selectedProject.end_date);
      if (start && end) return `${start} – ${end}`;
      if (start) return `Started ${start}`;
      if (end) return `Ends ${end}`;
      return '';
    }
  },

  created() {
    const stored = localStorage.getItem(this.selectionStorageKey);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed)) this.selectedProjectId = parsed;
    }
    const storedSubView = localStorage.getItem('projects_sub_view');
    if (storedSubView === 'view' || storedSubView === 'manage') {
      this.currentSubView = storedSubView;
    }
    this.loadProjects();
    this.loadActionTypes();
    this.loadEntityTypes();
  },

  watch: {
    selectionStorageKey() {
      this.projects = []; this.selectedProject = null; this.selectedProjectId = null;
      this.showCreateModal = false; this.showEditModal = false;
      this.loadProjects(); this.loadEntityTypes();
    }
  },
  methods: {
    async loadProjects() {
      const scope = this.selectionStorageKey;
      this.loadingProjects = true;
      this.projectLoadError = '';
      try {
        const response = await axios.get('api/graph-projects/');
        if (scope !== this.selectionStorageKey) return;
        this.projects = response.data.results || response.data;
        if (this.selectedProjectId && !this.projects.some(p => p.id === this.selectedProjectId)) {
          this.selectedProjectId = null;
          localStorage.removeItem(this.selectionStorageKey);
        }
        if (this.selectedProjectId) await this.reloadProject();
      } catch (error) {
        if (scope === this.selectionStorageKey) this.projectLoadError = error.response?.data?.detail || 'Could not load projects.';
      } finally {
        if (scope === this.selectionStorageKey) this.loadingProjects = false;
      }
    },

    async loadActionTypes() {
      const response = await axios.get('api/graph-action-types/');
      this.actionTypes = (response.data.results || response.data).filter(item => item.id === "action_item");
    },

    async loadEntityTypes() {
      const scope = this.selectionStorageKey;
      const response = await axios.get('api/entity-types/');
      if (scope === this.selectionStorageKey) this.entityTypes = response.data.results || response.data;
    },

    async reloadProject() {
      if (!this.selectedProjectId) {
        this.selectedProject = null;
        return;
      }
      const scope = this.selectionStorageKey;
      const id = this.selectedProjectId;
      const response = await axios.get(`api/graph-projects/${id}/`);
      if (scope !== this.selectionStorageKey || id !== this.selectedProjectId) return;
      this.selectedProject = response.data;
      // STANDARD projects have no Manage affordances; force back to View
      if (this.selectedProject?.project_type === 'STANDARD' && this.currentSubView === 'manage') {
        this.currentSubView = 'view';
      }
    },

    async onProjectChange() {
      this.showEditModal = false;
      if (this.selectedProjectId) {
        localStorage.setItem(this.selectionStorageKey, String(this.selectedProjectId));
        await this.reloadProject();
      } else {
        localStorage.removeItem(this.selectionStorageKey);
        this.selectedProject = null;
      }
    },

    onSubTabSelect(tab) {
      this.currentSubView = SUB_VIEW_BY_TAB[tab] || 'view';
      localStorage.setItem('projects_sub_view', this.currentSubView);
    },

    // ── Create modal ──────────────────────────────────────────────────

    openCreateModal() {
      this.showCreateModal = true;
    },

    closeCreateModal() {
      this.showCreateModal = false;
    },

    async onProjectCreated(project) {
      this.showCreateModal = false;
      await this.loadProjects();
      if (project?.id) {
        this.selectedProjectId = project.id;
        localStorage.setItem(this.selectionStorageKey, String(project.id));
        await this.reloadProject();
        this.currentSubView = 'manage';
        localStorage.setItem('projects_sub_view', 'manage');
      }
    },

    // ── Edit modal ──────────────────────────────────────────────────

    openEditModal() {
      this.editForm = {
        name: this.selectedProject.name,
        description: this.selectedProject.description || '',
        owner_entity_type: this.selectedProject.owner_entity_type || null
      };
      this.editError = '';
      this.showEditModal = true;
    },

    closeEditModal() {
      if (this.savingEdit) return;
      this.showEditModal = false;
      this.editError = '';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const d = calendarDate(dateString);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    async saveEdit() {
      if (!this.editForm.name.trim()) return;
      this.savingEdit = true;
      this.editError = '';

      try {
        await axios.patch(`api/graph-projects/${this.selectedProjectId}/`, {
          name: this.editForm.name.trim(),
          description: this.editForm.description,
          owner_entity_type: this.editForm.owner_entity_type
        });
        this.showEditModal = false;
        await this.reloadProject();
        const inList = this.projects.find(p => p.id === this.selectedProjectId);
        if (inList) inList.name = this.selectedProject.name;
      } catch (err) {
        this.editError = err.response?.data?.error || 'Failed to save changes.';
      } finally {
        this.savingEdit = false;
      }
    }
  }
};
</script>

<style scoped>
</style>
